import {
  acceptBookingTransfer,
  getBookingTransferRequest,
  rejectBookingTransfer,
} from 'flux/clients/bookings';
import { TransferRequest, TransferStatus } from 'model/booking/Transfers';
import React, { useContext, useEffect, useState } from 'react';
import {
  STATUS_ERROR,
  STATUS_INITIAL,
  STATUS_PENDING,
  STATUS_SUCCESS,
  StatusType,
} from '~/constants';

type TransferBookingContext = {
  status: StatusType;
  transferRequest: TransferRequest;
  transferStatus: TransferStatus;
  acceptTransfer: () => void;
  rejectTransfer: () => void;
};

const TransferContext = React.createContext<TransferBookingContext>({
  status: STATUS_INITIAL,
  transferRequest: null,
  transferStatus: TransferStatus.OPEN,
  acceptTransfer: async () => {},
  rejectTransfer: async () => {},
});

const fetchTransferData = async (transferRequestId: string) => {
  const transferRequest: TransferRequest =
    await getBookingTransferRequest(transferRequestId);
  return transferRequest;
};

function TransferBookingContextProvider({ transferRequestId, children }) {
  const [status, setStatus] = useState(STATUS_INITIAL);
  const [transferRequest, setTransferRequest] = useState<TransferRequest>();

  useEffect(() => {
    setStatus(STATUS_PENDING);
    fetchTransferData(transferRequestId)
      .then((transferRequest) => {
        setTransferRequest(transferRequest);
        setStatus(STATUS_SUCCESS);
      })
      .catch(() => {
        setStatus(STATUS_ERROR);
      });
  }, [transferRequestId]);

  const acceptTransfer = async () => {
    await acceptBookingTransfer(transferRequestId);
    const transferRequest: TransferRequest =
      await getBookingTransferRequest(transferRequestId);
    setTransferRequest(transferRequest);
  };

  const rejectTransfer = async () => {
    await rejectBookingTransfer(transferRequestId);
    const transferRequest: TransferRequest =
      await getBookingTransferRequest(transferRequestId);
    setTransferRequest(transferRequest);
  };

  const transferStatus =
    transferRequest?.status.status ?? TransferStatus.UNKNOWN;

  return (
    <TransferContext.Provider
      value={{
        status,
        transferRequest,
        transferStatus,
        acceptTransfer,
        rejectTransfer,
      }}
    >
      {children}
    </TransferContext.Provider>
  );
}

function useTransferBookingContext() {
  return useContext(TransferContext);
}

export {
  TransferContext,
  TransferBookingContextProvider,
  useTransferBookingContext,
};
